import React, { useEffect } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Chip,
    CircularProgress,
    Alert,
    LinearProgress,
    Avatar,
    Fade,
    Container,
    useTheme,
    alpha,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Assignment,
    Email,
    Send,
    Chat,
    CheckCircle,
    Pending,
    Error as ErrorIcon,
    Business,
    Star,
    Public,
    TrendingUp,
    ArrowUpward,
    ArrowDownward,
    MoreVert,
    Refresh,
} from '@mui/icons-material';
import { observer } from 'mobx-react-lite';
import { useStore } from '../store';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';

const Dashboard: React.FC = observer(() => {
    const { applicationStore } = useStore();
    const theme = useTheme();

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 30000); // Обновляем каждые 30 секунд
        return () => clearInterval(interval);
    }, []);

    const loadData = () => {
        applicationStore.fetchTodayStatistics();
        applicationStore.fetchApplications(1, 5);
    };

    const getStatusColor = (status: string) => {
        const colors = {
            'completed': 'success',
            'processing': 'info',
            'pending': 'warning',
            'failed': 'error',
        };
        return colors[status as keyof typeof colors] || 'default';
    };

    const getStatusText = (status: string) => {
        const texts = {
            'completed': 'Завершена',
            'processing': 'Обрабатывается',
            'pending': 'Ожидает',
            'failed': 'Ошибка',
        };
        return texts[status as keyof typeof texts] || status;
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'vip':
                return <Star sx={{ color: '#FFD700' }} />;
            case 'corporate':
                return <Business color="primary" />;
            default:
                return <Public color="action" />;
        }
    };

    const MetricCard = ({
                            title,
                            value,
                            icon,
                            color = 'primary',
                            trend,
                            subtitle,
                            percentage
                        }: {
        title: string;
        value: number;
        icon: React.ReactNode;
        color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
        trend?: 'up' | 'down' | 'flat';
        subtitle?: string;
        percentage?: number;
    }) => {
        const getTrendIcon = () => {
            if (!trend) return null;
            switch (trend) {
                case 'up':
                    return <ArrowUpward sx={{ fontSize: 16 }} />;
                case 'down':
                    return <ArrowDownward sx={{ fontSize: 16 }} />;
                default:
                    return <TrendingUp sx={{ fontSize: 16 }} />;
            }
        };

        const getTrendColor = () => {
            if (!trend) return 'text.secondary';
            return trend === 'up' ? 'success.main' : trend === 'down' ? 'error.main' : 'text.secondary';
        };

        return (
            <Card
                sx={{
                    height: '100%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 20px ${alpha(theme.palette[color].main, 0.2)}`,
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '100px',
                        height: '100px',
                        background: `radial-gradient(circle, ${alpha(theme.palette[color].main, 0.1)} 0%, transparent 70%)`,
                        transform: 'translate(30px, -30px)',
                    }
                }}
            >
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                        <Box flex={1}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    fontSize: '0.75rem'
                                }}
                            >
                                {title}
                            </Typography>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    color: theme.palette[color].main,
                                    mb: 0.5
                                }}
                            >
                                {value.toLocaleString()}
                            </Typography>
                            {subtitle && (
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                            {percentage !== undefined && (
                                <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            bgcolor: alpha(getTrendColor() === 'success.main' ? theme.palette.success.main : theme.palette.error.main, 0.1),
                                            color: getTrendColor()
                                        }}
                                    >
                                        {getTrendIcon()}
                                        <Typography variant="caption" fontWeight={600}>
                                            {percentage > 0 ? '+' : ''}{percentage}%
                                        </Typography>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        vs вчера
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                        <Avatar
                            sx={{
                                bgcolor: alpha(theme.palette[color].main, 0.1),
                                color: theme.palette[color].main,
                                width: 48,
                                height: 48,
                            }}
                        >
                            {icon}
                        </Avatar>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    if (applicationStore.loading) {
        return (
            <Container maxWidth="xl">
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
                    <CircularProgress size={60} thickness={4} />
                    <Typography variant="h6" color="textSecondary" sx={{ mt: 3 }}>
                        Загрузка панели управления...
                    </Typography>
                </Box>
            </Container>
        );
    }

    if (applicationStore.error) {
        return (
            <Container maxWidth="xl">
                <Alert severity="error" sx={{ mb: 2 }}>
                    {applicationStore.error}
                </Alert>
            </Container>
        );
    }

    const stats = applicationStore.todayStatistics;
    const emailVerificationRate = stats?.total_applications ?
        (stats.email_verified / stats.total_applications * 100).toFixed(1) : '0';
    const completionRate = stats?.total_applications ?
        (stats.completed_applications / stats.total_applications * 100).toFixed(1) : '0';

    return (
        <Container maxWidth="xl">
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Панель управления
                    </Typography>
                    <Tooltip title="Обновить данные">
                        <IconButton onClick={loadData} disabled={applicationStore.loading}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography variant="h6" color="textSecondary">
                    {new Date().toLocaleDateString('ru-RU', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </Typography>
            </Box>

            {/* Основные метрики */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={300}>
                        <Box>
                            <MetricCard
                                title="Всего заявок"
                                value={stats?.total_applications || 0}
                                icon={<Assignment />}
                                color="primary"
                                trend="up"
                                percentage={12}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={450}>
                        <Box>
                            <MetricCard
                                title="Email подтверждено"
                                value={stats?.email_verified || 0}
                                icon={<Email />}
                                color="success"
                                subtitle={`${emailVerificationRate}% от общего числа`}
                                trend="up"
                                percentage={8}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={600}>
                        <Box>
                            <MetricCard
                                title="Отправлено в CRM"
                                value={stats?.sent_to_crm || 0}
                                icon={<Send />}
                                color="info"
                                trend="flat"
                                percentage={0}
                            />
                        </Box>
                    </Fade>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Fade in timeout={750}>
                        <Box>
                            <MetricCard
                                title="В Mattermost"
                                value={stats?.sent_to_mattermost || 0}
                                icon={<Chat />}
                                color="warning"
                                trend="down"
                                percentage={-3}
                            />
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* График статусов */}
                <Grid item xs={12} md={4}>
                    <Fade in timeout={900}>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Typography variant="h6" fontWeight={700}>
                                    Статусы заявок
                                </Typography>
                                <IconButton size="small">
                                    <MoreVert />
                                </IconButton>
                            </Box>

                            <Box>
                                {[
                                    {
                                        label: 'Ожидают',
                                        value: stats?.pending_applications || 0,
                                        icon: <Pending />,
                                        color: 'warning' as const,
                                        percentage: stats?.total_applications ? ((stats?.pending_applications || 0) / stats.total_applications * 100) : 0
                                    },
                                    {
                                        label: 'Обрабатываются',
                                        value: stats?.processing_applications || 0,
                                        icon: <Assignment />,
                                        color: 'info' as const,
                                        percentage: stats?.total_applications ? ((stats?.processing_applications || 0) / stats.total_applications * 100) : 0
                                    },
                                    {
                                        label: 'Завершены',
                                        value: stats?.completed_applications || 0,
                                        icon: <CheckCircle />,
                                        color: 'success' as const,
                                        percentage: stats?.total_applications ? ((stats?.completed_applications || 0) / stats.total_applications * 100) : 0
                                    },
                                    {
                                        label: 'Ошибки',
                                        value: stats?.failed_applications || 0,
                                        icon: <ErrorIcon />,
                                        color: 'error' as const,
                                        percentage: stats?.total_applications ? ((stats?.failed_applications || 0) / stats.total_applications * 100) : 0
                                    },
                                ].map((item) => (
                                    <Box key={item.label} sx={{ mb: 3 }}>
                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: alpha(theme.palette[item.color].main, 0.1),
                                                        color: theme.palette[item.color].main
                                                    }}
                                                >
                                                    {React.cloneElement(item.icon, { fontSize: 'small' })}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {item.label}
                                                </Typography>
                                            </Box>
                                            <Box textAlign="right">
                                                <Typography variant="h6" fontWeight={700}>
                                                    {item.value}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.percentage.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={item.percentage}
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                bgcolor: alpha(theme.palette[item.color].main, 0.1),
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 3,
                                                    bgcolor: theme.palette[item.color].main,
                                                }
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Эффективность */}
                <Grid item xs={12} md={4}>
                    <Fade in timeout={1050}>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Typography variant="h6" fontWeight={700}>
                                    Эффективность обработки
                                </Typography>
                                <IconButton size="small">
                                    <MoreVert />
                                </IconButton>
                            </Box>

                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                    <CircularProgress
                                        variant="determinate"
                                        value={parseFloat(completionRate)}
                                        size={140}
                                        thickness={4}
                                        sx={{
                                            color: theme.palette.success.main,
                                            '& .MuiCircularProgress-circle': {
                                                strokeLinecap: 'round',
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            top: 0,
                                            left: 0,
                                            bottom: 0,
                                            right: 0,
                                            position: 'absolute',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="h3" fontWeight={700} color="success.main">
                                                {completionRate}%
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Завершено
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <Grid container spacing={2} sx={{ mt: 2 }}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight={700} color="info.main">
                                            {emailVerificationRate}%
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Email верификация
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            bgcolor: alpha(theme.palette.warning.main, 0.1),
                                            textAlign: 'center'
                                        }}
                                    >
                                        <Typography variant="h5" fontWeight={700} color="warning.main">
                                            {stats?.sent_to_crm || 0}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            В CRM
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>
                </Grid>

                {/* Последние заявки */}
                <Grid item xs={12} md={4}>
                    <Fade in timeout={1200}>
                        <Paper
                            sx={{
                                p: 3,
                                height: '100%',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                                <Typography variant="h6" fontWeight={700}>
                                    Последние заявки
                                </Typography>
                                <IconButton size="small">
                                    <MoreVert />
                                </IconButton>
                            </Box>

                            {applicationStore.recentApplications.length === 0 ? (
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        py: 8,
                                        opacity: 0.6
                                    }}
                                >
                                    <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                    <Typography variant="h6" color="textSecondary">
                                        Нет заявок за сегодня
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Пока не поступило ни одной заявки
                                    </Typography>
                                </Box>
                            ) : (
                                <List sx={{ p: 0 }}>
                                    {applicationStore.recentApplications.map((application, index) => (
                                        <ListItem
                                            key={application.id}
                                            sx={{
                                                px: 0,
                                                py: 2,
                                                borderBottom: index < applicationStore.recentApplications.length - 1 ? '1px solid' : 'none',
                                                borderColor: 'divider',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                                                }
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            {getTypeIcon(application.type)}
                                                            <Typography variant="body1" fontWeight={600}>
                                                                {application.full_name}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={getStatusText(application.status)}
                                                            color={getStatusColor(application.status) as any}
                                                            size="small"
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {application.company} • {application.tariff}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDistanceToNow(new Date(application.created_at), {
                                                                addSuffix: true,
                                                                locale: ru
                                                            })}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Fade>
                </Grid>
            </Grid>
        </Container>
    );
});

export default Dashboard;